import { FC, useMemo, type CSSProperties } from "react";
import { Alert, Card, Col, Row, Typography } from "antd";
import {
  normalizeCheckPriceCarrierList,
  type CheckPriceCarrierItem,
} from "services/apiSpeedShipping";
import TransportLogo from "components/ManageProduct/CreateProduct/ProductSKUCreate/TransportLogo";
import { formatWithCommas } from "components/ManageProduct/CreateProduct/ProductSKUCreate/helpers";
import {
  getCheckPriceCarrierCode,
  normalizeSpeedLogo,
} from "../../ParcelShared/speedCarrierUtils";

type ParcelCarrierPanelProps = {
  carriers: CheckPriceCarrierItem[];
  selectedCarrier: string;
  onSelectCarrier: (code: string) => void;
  speedHasSearched: boolean;
  loading?: boolean;
  /** ซ่อนหัวข้อย่อยเมื่ออยู่ในส่วน 4. เลือกบริษัทขนส่ง */
  showPanelTitle?: boolean;
};

const carrierPriceCellStyle: CSSProperties = {
  minWidth: 120,
  textAlign: "right",
  paddingLeft: 8,
  /** เว้นขวาหลังราคา/คำว่า บาท — ไม่ชิดขอบ card */
  paddingRight: 16,
};

/** เว้นช่องว่างรอบ logo ใน card — ไม่ให้ติดขอบซ้าย/ขวา (ลดด้านล่างเล็กน้อย) */
const carrierLogoWrapStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  padding: "8px 16px 2px 16px",
  boxSizing: "border-box",
};

const carrierCardBodyStyle: CSSProperties = {
  padding: "10px 20px 6px 20px",
};

const ParcelCarrierPanel: FC<ParcelCarrierPanelProps> = ({
  carriers,
  selectedCarrier,
  onSelectCarrier,
  speedHasSearched,
  loading,
  showPanelTitle = true,
}) => {
  const carrierList = normalizeCheckPriceCarrierList(carriers);
  const selectedItem = useMemo(
    () =>
      carrierList.find((c) => getCheckPriceCarrierCode(c) === selectedCarrier),
    [carrierList, selectedCarrier]
  );

  return (
    <div>
      {showPanelTitle && (
        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          รายการขนส่งให้เลือก
        </Typography.Title>
      )}

      {!speedHasSearched && !loading && (
        <Alert
          type="info"
          showIcon
          message='ระบบจะแสดงรายการขนส่ง เมื่อคลิกปุ่ม "ค้นหาบริษัทขนส่ง"'
        />
      )}

      {loading && (
        <Alert type="info" showIcon message="กำลังโหลดค่าขนส่ง..." style={{ marginBottom: 8 }} />
      )}

      {speedHasSearched && selectedItem && (
        <Card
          size="small"
          bodyStyle={carrierCardBodyStyle}
          style={{ marginBottom: 4, background: "#fafafa", border: "1px solid #d9d9d9" }}
        >
          <Row align="middle" justify="space-between" gutter={12} wrap={false}>
            <Col flex="none">
              <div style={carrierLogoWrapStyle}>
                <TransportLogo
                  src={normalizeSpeedLogo(selectedItem.logo)}
                  alt={selectedItem.name || "carrier"}
                  height={36}
                  width={64}
                />
              </div>
            </Col>
            <Col flex="auto">
              <Typography.Text strong>ขนส่งที่เลือก</Typography.Text>
              <div style={{ marginTop: 4, fontWeight: 600 }}>
                {selectedItem.name || selectedCarrier}
              </div>
            </Col>
            <Col>
              <div style={carrierPriceCellStyle}>
                <Typography.Text strong>
                  {formatWithCommas(
                    Math.ceil(Number(selectedItem.total ?? selectedItem.price ?? 0))
                  )}{" "}
                  บาท
                </Typography.Text>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {speedHasSearched && (
        <div style={{ maxHeight: 320, overflowY: "auto", paddingRight: 12 }}>
          <Row gutter={[10, 6]}>
            {carrierList.map((item) => {
              const code = getCheckPriceCarrierCode(item);
              if (!code) return null;
              const active = code === selectedCarrier;
              const total = Number(item.total ?? item.price ?? 0);
              return (
                <Col key={code} span={24}>
                  <Card
                    size="small"
                    hoverable
                    bodyStyle={carrierCardBodyStyle}
                    onClick={() => onSelectCarrier(code)}
                    style={{
                      border: active ? "1px solid #1890ff" : "1px solid #f0f0f0",
                      background: active ? "#e6f7ff" : "#fff",
                      minHeight: 72,
                      cursor: "pointer",
                    }}
                  >
                    <Row align="middle" justify="space-between" gutter={12} wrap={false}>
                      <Col flex="none">
                        <div style={carrierLogoWrapStyle}>
                          <TransportLogo
                            src={normalizeSpeedLogo(item.logo)}
                            alt={item.name || code}
                            height={40}
                            width={64}
                          />
                        </div>
                      </Col>
                      <Col flex="auto">
                        <Typography.Text strong>{item.name || code}</Typography.Text>
                      </Col>
                      <Col>
                        <div style={carrierPriceCellStyle}>
                          <Typography.Text strong>
                            {formatWithCommas(Math.ceil(total))} บาท
                          </Typography.Text>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              );
            })}
          </Row>
          {carrierList.length === 0 && !loading && (
            <Typography.Text type="secondary">ไม่พบรายการขนส่งสำหรับเส้นทางนี้</Typography.Text>
          )}
        </div>
      )}
    </div>
  );
};

export default ParcelCarrierPanel;
